<template>
  <div
    :role="interactive ? 'button' : undefined"
    :tabindex="interactive ? 0 : undefined"
    class="notification-card bg-white border rounded-lg pa-3"
    :class="{ 'cursor-pointer': interactive }"
    @click="onRootClick"
    @keydown.enter.prevent="onRootClick"
  >
    <div
      class="notification-card__icon d-inline-flex align-center justify-center flex-shrink-0 rounded"
    >
      <VIcon
        :icon="iconConfig.icon"
        size="24"
        :style="{ color: iconConfig.iconColor }"
      />
    </div>

    <div class="notification-card__content d-flex flex-column ga-1 min-w-0">
      <span
        class="font-weight-bold text-right text-truncate"
        :class="notification.isRead ? 'text-blue-gray-800' : 'text-gray-700'"
      >
        {{ notification.title }}
      </span>
      <span
        v-if="displayBody"
        class="text-sm text-right text-truncate"
        :class="
          notification.isRead ? 'text-blue-gray-700' : 'text-blue-gray-900'
        "
      >
        {{ displayBody }}
      </span>
    </div>

    <div
      class="notification-card__meta d-flex flex-column align-center flex-shrink-0"
      :class="
        showDelete
          ? 'justify-space-between'
          : 'justify-center notification-card__meta--time-only'
      "
    >
      <span
        v-if="timeLabel"
        class="notification-card__time text-gray-500 text-center"
      >
        {{ timeLabel }}
      </span>

      <button
        v-if="showDelete"
        type="button"
        class="d-inline-flex align-center justify-center border-0 bg-transparent cursor-pointer pa-0"
        style="width: 24px; height: 24px"
        aria-label="حذف اعلان"
        @click.stop="$emit('delete', notification.id)"
      >
        <VIcon icon="delete" size="18" color="blue-gray-600" />
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { VIcon } from 'vuetify/components'
import type { Emits, Props } from './NotificationCard.types'
import { NOTIFICATION_ICON_CONFIG } from '@/modules/Notification/constants/notificationIcons'
import { formatNotificationTime } from '@/modules/Notification/utils/formatNotificationTime'

defineOptions({
  name: 'NotificationCard',
})

const props = withDefaults(defineProps<Props>(), {
  showDelete: true,
  interactive: true,
})

const emit = defineEmits<Emits>()

const displayBody = computed(
  () => props.notification.body ?? props.notification.subtitle
)

const timeLabel = computed(() =>
  formatNotificationTime(props.notification.createdAt)
)

function onRootClick() {
  if (!props.interactive) {
    return
  }

  onCardClick()
}

function onCardClick() {
  emit('open', props.notification)
}

const iconConfig = computed(
  () => NOTIFICATION_ICON_CONFIG[props.notification.type]
)
</script>

<style scoped lang="scss">
.notification-card {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 54px;
  gap: 10px;
  align-items: center;
  min-width: 0;
}

.notification-card__icon {
  width: 40px;
  height: 40px;
  background-color: #f4f4f7;
}

.notification-card__meta {
  min-height: 40px;
  width: 54px;
}

.notification-card__meta--time-only {
  min-height: auto;
}

.notification-card__time {
  font-size: 10px;
  line-height: 1.8;
  width: 100%;
  word-break: break-word;
}
</style>
